import classNames from 'classnames'
import React from 'react'
import { Link, LinkProps, To } from 'react-router-dom'

import { getNavigationLinkUrl } from '../../../../main/util'
import { QuoteContext } from '../../QuoteContext'
import { QuoteQuery, QuoteSubview, QuoteView } from '../../types'
import { QuoteLinksContext, QuoteLinksContextType } from './QuoteLinksWrapper'

interface NavigationLinkProps extends Omit<LinkProps, 'to'> {
  /**
   * Keys to preserve from the current URL query. Runs first when manipulating the URL
   *
   * @default [Ticker, Type, Period, DateRange]
   */
  preserveQueryKeys?: Array<QuoteQuery & string>

  /**
   * Keys to omit from the current URL query. Runs after `preserveQueryKeys`
   */
  removeQueryKeys?: Array<QuoteQuery & string>

  /**
   * Additional values to set to the URL query after preserving/removing
   *
   * @default { Type: props.view }
   */
  additionalQueryValues?: Partial<Record<QuoteQuery, string | number>>

  /**
   * The view the component links to
   */
  view: QuoteView | QuoteSubview

  getLink?: never
}

interface CustomNavigationLinkProps extends Omit<LinkProps, 'to'> {
  view?: never
  preserveQueryKeys?: never
  removeQueryKeys?: never
  additionalQueryValues?: never

  /**
   * Custom function to get the link
   */
  getLink: (context: QuoteLinksContextType) => To
}

export function NavigationLink({
  view,
  getLink,
  reloadDocument = true,
  preserveQueryKeys = [QuoteQuery.Ticker, QuoteQuery.Type, QuoteQuery.Period, QuoteQuery.DateRange],
  removeQueryKeys,
  additionalQueryValues,
  ...props
}: React.PropsWithChildren<NavigationLinkProps | CustomNavigationLinkProps>) {
  const quoteContext = React.useContext(QuoteContext)
  const linkContext = React.useContext(QuoteLinksContext)

  const linkUrl = React.useMemo(() => {
    if (typeof getLink === 'function') return getLink(linkContext)

    return getNavigationLinkUrl(
      linkContext.location,
      preserveQueryKeys,
      removeQueryKeys,
      additionalQueryValues ?? { [QuoteQuery.Type]: view }
    ).toString()
  }, [additionalQueryValues, linkContext, getLink, preserveQueryKeys, removeQueryKeys, view])

  return (
    <Link
      {...props}
      to={linkUrl}
      reloadDocument={reloadDocument}
      className={classNames(props.className, 'tab-link', {
        'font-semibold': quoteContext.view === view || quoteContext.subview === view,
      })}
    >
      {props.children}
    </Link>
  )
}
